import React from 'react';
import { Divider, Icon, IconType, Text } from '@/ui';
import styles from './styles.module.css';
import accountStyles from '../../styles.module.css';
import { useTranslation } from 'react-i18next';
import { Column, Row } from '@/components/SettingsLayout';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetPlansQuery } from '@/store/slices/plansSlice';
import { Plan } from '@/services/Plans/plans.service';
import { FeatureGroupEnum, UNLIMITED_VALUE } from '@/constants/constants';
import { FeatureType } from '@/services/Features/features.service';
import { useGetUser } from '@/hooks';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetAllExtensionsQuery } from '@/store';
import { Extension } from '@/services/Extensions/extensions.service';
import { Skeleton } from '@/components/ui/Skeleton';
import { formatTestId } from '@/utils/helpers/formatTestId';

function PlansList() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { company, user } = useGetUser();
  const { data, isLoading: plansLoading } = useGetPlansQuery(
    company?._id
      ? {
          organizationId: company?._id,
        }
      : skipToken,
  );
  const { data: availableExtensions, isLoading: extensionsLoading } = useGetAllExtensionsQuery({
    organizationId: user?.targetOrganizationId || '',
  });
  const navigate = useNavigate();
  const addNewPlan = () => {
    navigate(`${pathname}/plan?mode='create'&tab=details`);
  };

  const editPlan = (planId: string) => {
    navigate(`${pathname}/plan?mode='edit'&planId=${planId}&tab=details`);
  };

  if (plansLoading || extensionsLoading) {
    const skeletonCount = 8;
    return (
      <div className={styles.plansGridSkeleton}>
        {Array.from({ length: skeletonCount }, (_, i) => (
          <Skeleton key={i} className='h-[440px] min-w-[255px] rounded-md' />
        ))}
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className={styles.plansGrid}>
        <div className={styles.planCard} onClick={addNewPlan} data-testid='add-new-plan-button'>
          <Column fullHeight align='center' justify='justify-center'>
            <div className={accountStyles.bluePlus}>
              <Icon type={IconType.Plus} width={20} />
            </div>
            <Text size='xs' weight='light-medium'>
              {t('Shared.add_new')}
            </Text>
          </Column>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.plansGrid}>
      {data.map((plan, index) => {
        return (
          <PlanCard
            key={index}
            plan={plan}
            onEdit={editPlan}
            availableExtensions={availableExtensions}
          />
        );
      })}
      <div
        className={`${styles.planCard} ${accountStyles.addNew}`}
        onClick={addNewPlan}
        data-testid='add-new-plan-button'
      >
        <Column fullHeight align='center' justify='justify-center'>
          <div className={accountStyles.bluePlus}>
            <Icon type={IconType.Plus} width={20} />
          </div>
          <Text size='xs' weight='light-medium'>
            {t('Shared.add_new')}
          </Text>
        </Column>
      </div>
    </div>
  );
}

export default PlansList;

type PlanProps = {
  plan: Plan;
  onEdit: (planId: string) => void;
  availableExtensions: Extension[] | undefined;
};

export const FEATURE_GROUP_ORDER = [
  FeatureGroupEnum.ACCOUNT_RESTRICTIONS,
  FeatureGroupEnum.MENU_RESTRICTIONS,
];

const PlanCard = ({ plan, onEdit, availableExtensions }: PlanProps) => {
  const { t } = useTranslation();

  const orderedFeatureGroups = FEATURE_GROUP_ORDER.filter(
    (groupId) => !!plan.featureGroups[groupId],
  );

  return (
    <div className={styles.planCard}>
      <div className={styles.planHeader}>
        <Text size='s' weight='medium' className='truncate'>
          {plan.name ?? t('Accounts.plans_name')}
        </Text>
        <div className={styles.editBtn} onClick={() => onEdit(plan._id)}>
          <Icon type={IconType.Pencil_Edit} width={24} height={24} fill='transparent' />
        </div>
      </div>

      <div className={styles.planFeatures}>
        <Column gapSize='12' fullWidth>
          {orderedFeatureGroups.map((groupId, index) => (
            <React.Fragment key={groupId}>
              {index > 0 && <Divider />}
              <FeatureGroupSection featureGroup={plan.featureGroups[groupId]} groupId={groupId} />
            </React.Fragment>
          ))}
          <>
            {orderedFeatureGroups.length > 0 && <Divider />}
            <Column gapSize='8' fullWidth>
              {availableExtensions?.map((extension) => (
                <Row key={extension._id} fullWidth justify='justify-between' align='center'>
                  <Row align='center'>
                    <div
                      style={{
                        flexShrink: 0,
                        minWidth: '24px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Icon
                        type={getIconForExtensionCategory(extension.category)}
                        width={24}
                        height={24}
                        fill='transparent'
                      />
                    </div>
                    <Text size='xs' weight='light-medium'>
                      {t(`Extensions.${extension.label}`, {
                        defaultValue: extension.label,
                      })}
                    </Text>
                  </Row>

                  {!plan.extensions ||
                  plan.extensions?.some((planExtension) => planExtension._id === extension._id) ? (
                    <div
                      className={styles.enabledExtension}
                      style={{
                        flexShrink: 0,
                        minWidth: '24px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      data-testid={`enabled-extension-icon-${extension.label}`}
                    >
                      <Icon type={IconType.CheckTick} width={24} height={24} fill='transparent' />
                    </div>
                  ) : (
                    <div
                      className={styles.disabledExtension}
                      style={{
                        flexShrink: 0,
                        minWidth: '24px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Icon type={IconType.CircleCross} width={24} height={24} fill='transparent' />
                    </div>
                  )}
                </Row>
              ))}
            </Column>
          </>

          <Divider />
          <Row fullWidth justify='justify-between'>
            <Text size='xs' weight='light-medium'>
              {t('Accounts.available_checkout_flows')}
            </Text>
            <div className={styles.enabledExtension + ' ' + styles.plansTextRow}>
              {plan.checkoutFlows === null ? (
                <Text size='xs' weight='light-medium' testId='available-checkout-flows-all'>
                  {t('Shared.all')}
                </Text>
              ) : (
                <Text size='xs' weight='light-medium'>
                  {Array.isArray(plan.checkoutFlows) ? plan.checkoutFlows.length : 0}
                </Text>
              )}
            </div>
          </Row>
        </Column>
      </div>
    </div>
  );
};

const getIconForExtensionCategory = (category: string): IconType => {
  switch (category) {
    case 'payment':
      return IconType.Payments;
    case 'sync':
      return IconType.Chain;
    case 'hub':
      return IconType.StockManagement;
    default:
      return IconType.Payments;
  }
};

type PlanFeature = {
  _id: string;
  name: string;
  value: string;
  description?: string;
  type: FeatureType;
};

type FeatureGroupData = {
  _id: string;
  name: string;
  features: PlanFeature[];
};

type FeatureGroupSectionProps = {
  featureGroup: FeatureGroupData;
  groupId?: string;
};

const FeatureGroupSection = ({ featureGroup }: FeatureGroupSectionProps) => {
  const { t } = useTranslation();

  if (!featureGroup?.features || featureGroup.features.length === 0) {
    return null;
  }

  /**
   * Returns the appropriate icon for the feature or false if no icon is needed
   * @param feature The feature to get the icon for
   * @returns IconType or false
   */
  const getIconForFeature = (feature: PlanFeature): IconType | false => {
    switch (feature.type) {
      case FeatureType.BOOLEAN:
        return feature.value === '1' ? IconType.CheckTick : IconType.CircleCross;
      case FeatureType.NUMBER:
        return feature.value === UNLIMITED_VALUE ? IconType.Infinity : false;
      default:
        return false;
    }
  };

  return (
    <Column gapSize='8' fullWidth>
      {featureGroup.features.map((feature: PlanFeature) => {
        const icon = getIconForFeature(feature);
        return (
          <div className={styles.featureRow} key={feature._id}>
            <Text size='xs' weight='light-medium'>
              {t(`Features.${feature.name}`)}
            </Text>
            {icon ? (
              <div
                className={
                  icon === IconType.CheckTick || icon === IconType.Infinity
                    ? styles.enabledExtension
                    : styles.disabledExtension
                }
                data-testid={formatTestId('feature-icon', feature.name)}
              >
                <Icon type={icon} width={24} height={24} fill='transparent' />
              </div>
            ) : (
              <div className={styles.enabledExtension + ' ' + styles.plansTextRow}>
                <Text size='xs' weight='light-medium'>
                  {feature?.value}
                </Text>
              </div>
            )}
          </div>
        );
      })}
    </Column>
  );
};
